/**
 * `BULK_COMPLETE` step. Single-call wrapper over
 * `PUT /api/v1/core/work-orders/bulk-complete` (`bulkComplete`) — marks the
 * given work orders with a target status (`'Serviced'` today). Atomic on the
 * BE and daily-only; never touches the master sequence. The endpoint has no
 * `skipInvalidate` knob, so the terminal INVALIDATE step owns the refetch.
 */
import { err, ok } from 'types/entities/shared/result'

import { extractBackendError } from '../backendError'

import type { StepResult } from '../types'
import type { BulkCompleteBody } from 'types/api/route/bulkRequests'
import type { Deps, PipelineStep } from 'types/modules/routing'

export interface BulkCompleteStepArgs {
  readonly body: BulkCompleteBody
}

export type BulkCompleteStepValue = { readonly committed: true }

export type BulkCompleteStepResult = StepResult<'BULK_COMPLETE', BulkCompleteStepValue>

const STEP_ID = 'bulk-complete'

export const makeBulkCompleteStep = (args: BulkCompleteStepArgs, deps: Deps): PipelineStep<'BULK_COMPLETE', BulkCompleteStepResult> => {
  return {
    id: STEP_ID,
    phase: 'BULK_COMPLETE',
    label: 'Failed to bulk-complete stops',
    applyOrder: 200,
    run: async () => {
      if (!deps.bulkComplete) {
        return err({
          kind: 'PRECONDITION_FAILED',
          reason: 'bulkComplete dep not provided — the complete intent requires deps.bulkComplete to be wired',
        })
      }
      try {
        await deps.bulkComplete(args.body).unwrap()
        return ok({ phase: 'BULK_COMPLETE', stepId: STEP_ID, value: { committed: true } })
      } catch (cause: unknown) {
        const { status, message } = extractBackendError(cause)
        return err({
          kind: 'BE_REJECTED',
          phase: 'BULK_COMPLETE',
          status,
          message,
        })
      }
    },
  }
}
